<script setup lang="ts">
export type NoticeVariant = "info";

interface Props {
  /** Bold heading line. */
  title?: string;
  /** Supporting text below the title. */
  body?: string;
  /**
   * Visual tone. Only `info` (blue) is defined today; the prop exists so the
   * banner can grow success/warning tones without a breaking change.
   */
  variant?: NoticeVariant;
}

withDefaults(defineProps<Props>(), {
  title: "",
  body: "",
  variant: "info",
});
</script>

<template>
  <div class="pj-notice" :class="`pj-notice--${variant}`" data-testid="pj-notice">
    <span class="pj-notice__icon" aria-hidden="true">
      <svg viewBox="0 0 20 20" width="20" height="20" focusable="false">
        <path
          fill="currentColor"
          d="M10 1.667A8.333 8.333 0 1 0 10 18.333 8.333 8.333 0 0 0 10 1.667Zm0 3.75a1.042 1.042 0 1 1 0 2.083 1.042 1.042 0 0 1 0-2.083Zm1.25 9.166h-2.5a.833.833 0 0 1 0-1.666h.417V10h-.417a.833.833 0 0 1 0-1.667h1.25c.46 0 .833.373.833.834v3.75h.417a.833.833 0 0 1 0 1.666Z"
        />
      </svg>
    </span>
    <div class="pj-notice__text">
      <p v-if="title" class="pj-notice__title" data-testid="pj-notice-title">{{ title }}</p>
      <p v-if="body" class="pj-notice__body" data-testid="pj-notice-body">{{ body }}</p>
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pj-notice {
  display: flex;
  align-items: center;
  gap: var(--pj-space-3);
  padding: var(--pj-space-3);
  border-radius: var(--pj-radius-2xl);
}

.pj-notice--info {
  background: var(--pj-color-brand-blue-50, #ecf0ff);
}

.pj-notice__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
}

.pj-notice--info .pj-notice__icon {
  color: var(--pj-color-brand-blue-500, #5877ff);
}

.pj-notice__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-sm);
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.pj-notice__title,
.pj-notice__body {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
}

.pj-notice__title {
  font-weight: 600;
}

.pj-notice__body {
  font-weight: 400;
  letter-spacing: -0.5px;
}
</style>
